/**
 * ISSUE-448: a browser can measure the six components whose 4/8-ladder
 * spacing edits the guard forced, but that the local harness cannot seed
 * with real data (no award grants, no award types, no inbox items). This
 * route renders each one standalone with worst-case fixture props, so
 * `tests/e2e/src/app/spacingLadderGeometry.e2e.ts` can measure that its
 * content still fits its box. It is signed in and linked from no
 * navigation, following the `/gallery`, `/gallery/marks` and
 * `/gallery/neutral-surfaces` precedent.
 *
 * The file is named `gallery_.spacing-ladder` so this route stays out of
 * `/gallery`'s tree: `/gallery` renders no Outlet, so a nested route would
 * never paint. Same trap `gallery_.marks.tsx` and
 * `gallery_.neutral-surfaces.tsx` both record.
 */
import { createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { requireSignedIn } from "../auth/requireSignedIn";
import { AwardChipDialog } from "../features/accountHome/AwardChipDialog";
import type { typeAwardChip } from "../features/awards/awardChips";
import {
  NotificationRow,
  type typeNotificationRowItem,
} from "../features/notifications/NotificationRow";
import {
  AwardTypeFormSheet,
  type typeAwardTypeFormSheetEditing,
} from "../features/poolAwards/AwardTypeFormSheet";
import { AwardTypeRemoveFlow } from "../features/poolAwards/AwardTypeRemoveFlow";
import type { typeAwardKind } from "../features/poolAwards/poolAwardsCopy";
import type { typeAwardTypeRow } from "../features/poolAwards/poolAwardsModel";
import { GetTheAppBanner } from "../features/pools/GetTheAppBanner";
import type { typeStoreAvailability } from "../shell/appStoreLinks";

// The container class the real gallery routes share, copied verbatim from
// gallery_.neutral-surfaces.tsx:31.
const SURFACE_CARD_CLASS =
  "flex flex-col gap-3 rounded-lg border border-line bg-surface p-4";

const NOTIFICATION_ROW_ITEM: typeNotificationRowItem = {
  inboxItemId: "spacing-inbox-1",
  group: "invite",
  title:
    "You have been invited to join the Sunday Night Survivor Pool for the 2026 season",
  body:
    "Your friend Alex started a new pool and wants you to join before the first" +
    " round locks. Pick winners each week, avoid picking the same team twice, and" +
    " survive as long as you can. The pool closes to new entries once the first" +
    " game of the season kicks off, so respond soon if you want in.",
  isRead: false,
  createdAtIso: "2026-08-01T12:00:00.000Z",
};

const NOTIFICATION_ROW_NOW = new Date("2026-08-27T12:00:00.000Z");

// ISSUE-1185: a read row, so the e2e spec can check it starts its icon at the same x as the unread row above.
const NOTIFICATION_ROW_READ_ITEM: typeNotificationRowItem = {
  inboxItemId: "spacing-inbox-2",
  group: "pools",
  title: "Your pool needs you",
  body: "Submit your picks before the round locks.",
  isRead: true,
  createdAtIso: "2026-08-27T11:55:00.000Z",
};

const AWARD_CHIP: typeAwardChip = {
  awardGrantId: "spacing-grant-1",
  poolId: "spacing-pool-1",
  contestName: "2026 Survivor Season Championship Round",
  kind: "medal",
  source: "earned",
  name: "Season Champion",
  color: "color1",
  isThisSeason: true,
};

const AWARD_CHIP_POOL_NAME =
  "Sunday Night Survivor Pool, presented by the neighborhood league";

const APP_BANNER_USER_AGENT =
  "Mozilla/5.0 (iPhone; CPU iPhone OS 18_0 like Mac OS X) AppleWebKit/605.1.15";

const APP_BANNER_AVAILABILITY: typeStoreAvailability = {
  iosStoreUrl: "https://apps.apple.com/app/id0000000000",
};

const AWARD_KIND: typeAwardKind = "medal";

const AWARD_FORM_EDITING: typeAwardTypeFormSheetEditing = {
  awardTypeId: "spacing-award-type-1",
  name: "Most Correct Picks in the Regular Season Standings",
  color: "color7",
  status: "archived",
  grantCount: 12,
};

const AWARD_REMOVE_TARGET_ROW: typeAwardTypeRow = {
  awardTypeId: "spacing-award-type-2",
  name: "Most Correct Picks in the Regular Season Standings",
  color: "color3",
  status: "active",
  grantCount: 12,
  memberCount: 30,
};

function SpacingLadderGallery(): ReactNode {
  return (
    <div
      data-testid="spacing-ladder-gallery"
      className="flex flex-col gap-6 p-6"
    >
      <section
        data-testid="spacing-notification-row"
        className={SURFACE_CARD_CLASS}
      >
        <h2 className="text-h2 text-ink">NotificationRow</h2>
        <NotificationRow
          item={NOTIFICATION_ROW_ITEM}
          now={NOTIFICATION_ROW_NOW}
          onOpen={() => undefined}
        />
      </section>

      <section
        data-testid="spacing-notification-row-read"
        className={SURFACE_CARD_CLASS}
      >
        <h2 className="text-h2 text-ink">NotificationRow, read</h2>
        <NotificationRow
          item={NOTIFICATION_ROW_READ_ITEM}
          now={NOTIFICATION_ROW_NOW}
          onOpen={() => undefined}
        />
      </section>

      <section
        data-testid="spacing-award-chip-dialog"
        className={SURFACE_CARD_CLASS}
      >
        <h2 className="text-h2 text-ink">AwardChipDialog</h2>
        <AwardChipDialog
          open={true}
          onClose={() => undefined}
          chip={AWARD_CHIP}
          poolName={AWARD_CHIP_POOL_NAME}
        />
      </section>

      <section data-testid="spacing-app-banner" className={SURFACE_CARD_CLASS}>
        <h2 className="text-h2 text-ink">GetTheAppBanner</h2>
        <GetTheAppBanner
          userAgent={APP_BANNER_USER_AGENT}
          availability={APP_BANNER_AVAILABILITY}
          onDismiss={() => undefined}
        />
      </section>

      <section
        data-testid="spacing-award-form-sheet"
        className={SURFACE_CARD_CLASS}
      >
        <h2 className="text-h2 text-ink">AwardTypeFormSheet</h2>
        <AwardTypeFormSheet
          open={true}
          poolId="spacing-pool-1"
          kind={AWARD_KIND}
          editing={AWARD_FORM_EDITING}
          onOpenChange={() => undefined}
          onSaved={() => undefined}
        />
      </section>

      <section
        data-testid="spacing-award-remove-flow"
        className={SURFACE_CARD_CLASS}
      >
        <h2 className="text-h2 text-ink">AwardTypeRemoveFlow</h2>
        <AwardTypeRemoveFlow
          poolId="spacing-pool-1"
          target={{ row: AWARD_REMOVE_TARGET_ROW, kind: AWARD_KIND }}
          onDone={() => undefined}
        />
      </section>
    </div>
  );
}

export const Route = createFileRoute("/gallery_/spacing-ladder")({
  beforeLoad: requireSignedIn,
  component: SpacingLadderGallery,
});
